export interface VideoCompressOptions {
  /** 超过该大小才压缩，默认 10MB */
  minSize?: number;
  /** 最大宽度，默认 960 */
  maxWidth?: number;
  /** 最大高度，默认 540 */
  maxHeight?: number;
  /** 视频码率，默认 1Mbps */
  videoBitsPerSecond?: number;
  /** 压缩播放倍速，默认 4 倍（显著缩短等待时间） */
  playbackRate?: number;
  /** 超过该时长跳过压缩直接上传，默认 5 分钟 */
  maxDuration?: number;
  onProgress?: (percent: number) => void;
  onPhaseChange?: (phase: 'compressing' | 'encoding') => void;
}

const DEFAULT_MIN_SIZE = 10 * 1024 * 1024;
const DEFAULT_MAX_DURATION = 5 * 60;
const DEFAULT_PLAYBACK_RATE = 4;
const CAPTURE_FPS = 24;
const VIDEO_EXT_REG = /\.(mp4|mov|avi|mkv|webm|m4v|flv|wmv)$/i;

export function isVideoFile(file: File): boolean {
  if (file.type.startsWith('video/')) {
    return true;
  }
  return VIDEO_EXT_REG.test(file.name);
}

export function shouldCompressVideo(file: File, minSize = DEFAULT_MIN_SIZE): boolean {
  return isVideoFile(file) && file.size > minSize;
}

function calcScaledSize(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } {
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height };
  }
  const ratio = Math.min(maxWidth / width, maxHeight / height);
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

function getSupportedMimeType(): string {
  const candidates = [
    'video/webm;codecs=vp8',
    'video/webm;codecs=vp9',
    'video/webm',
    'video/mp4',
  ];
  return candidates.find(type => MediaRecorder.isTypeSupported(type)) || 'video/webm';
}

function getVideoCaptureStream(video: HTMLVideoElement): MediaStream | null {
  const captureStream =
    (video as HTMLVideoElement & { captureStream?: () => MediaStream }).captureStream ||
    (video as HTMLVideoElement & { mozCaptureStream?: () => MediaStream }).mozCaptureStream;
  return captureStream ? captureStream.call(video) : null;
}

/**
 * 使用 Canvas **MediaRecorder** 对视频进行前端压缩。
 * 压缩失败或不支持时返回原文件。
 */
export async function compressVideo(
  file: File,
  options: VideoCompressOptions = {},
): Promise<File> {
  const {
    minSize = DEFAULT_MIN_SIZE,
    maxWidth = 960,
    maxHeight = 540,
    videoBitsPerSecond = 1000000,
    playbackRate = DEFAULT_PLAYBACK_RATE,
    maxDuration = DEFAULT_MAX_DURATION,
    onProgress,
    onPhaseChange,
  } = options;

  if (!shouldCompressVideo(file, minSize)) {
    return file;
  }

  if (typeof window === 'undefined' || !window.MediaRecorder) {
    return file;
  }

  const canvasProto = HTMLCanvasElement.prototype as HTMLCanvasElement & {
    captureStream?: (frameRate?: number) => MediaStream;
  };
  if (!canvasProto.captureStream) {
    return file;
  }

  return new Promise(resolve => {
    const video = document.createElement('video');
    video.playsInline = true;
    video.preload = 'auto';
    video.muted = true;

    const objectUrl = URL.createObjectURL(file);
    let animationId = 0;
    let recorder: MediaRecorder | null = null;

    const cleanup = () => {
      cancelAnimationFrame(animationId);
      video.pause();
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(objectUrl);
    };

    const fallback = (reason?: unknown) => {
      cleanup();
      if (reason) {
        console.warn('视频压缩失败，将上传原文件', reason);
      }
      resolve(file);
    };

    video.onerror = () => fallback(new Error('video load error'));

    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      if (duration <= 0) {
        fallback(new Error('invalid video duration'));
        return;
      }

      if (duration > maxDuration) {
        fallback(new Error(`video too long (${duration}s), skip compress`));
        return;
      }

      const { width, height } = calcScaledSize(
        video.videoWidth,
        video.videoHeight,
        maxWidth,
        maxHeight,
      );

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        fallback(new Error('canvas context unavailable'));
        return;
      }

      const mimeType = getSupportedMimeType();
      const canvasStream = canvasProto.captureStream.call(canvas, CAPTURE_FPS);
      const chunks: BlobPart[] = [];
      let hasStoppedRecorder = false;

      const stopRecorder = () => {
        if (hasStoppedRecorder) {
          return;
        }
        hasStoppedRecorder = true;
        onPhaseChange?.('encoding');
        onProgress?.(99);
        if (recorder && recorder.state !== 'inactive') {
          recorder.stop();
        }
      };

      video.onended = stopRecorder;

      video
        .play()
        .then(() => {
          try {
            video.playbackRate = playbackRate;
          } catch {
            video.playbackRate = 1;
          }
          const effectivePlaybackRate = Math.max(1, video.playbackRate || 1);

          const videoStream = getVideoCaptureStream(video);
          const outputStream = new MediaStream([...canvasStream.getVideoTracks()]);
          // 加速播放时音频会失真，仅 1 倍速时保留音轨
          if (effectivePlaybackRate <= 1) {
            videoStream?.getAudioTracks().forEach(track => outputStream.addTrack(track));
          }

          const targetBitrate = Math.max(
            500000,
            Math.min(
              videoBitsPerSecond,
              Math.round(((file.size * 8) / duration) * 0.6),
            ),
          );

          recorder = new MediaRecorder(outputStream, {
            mimeType,
            videoBitsPerSecond: targetBitrate,
          });

          recorder.ondataavailable = event => {
            if (event.data.size > 0) {
              chunks.push(event.data);
            }
          };

          recorder.onstop = () => {
            cleanup();
            const blob = new Blob(chunks, { type: mimeType });
            if (!blob.size || blob.size >= file.size * 0.95) {
              resolve(file);
              return;
            }

            const ext = mimeType.includes('mp4') ? '.mp4' : '.webm';
            const baseName = file.name.replace(/\.[^.]+$/, '') || 'video';
            onProgress?.(100);
            resolve(
              new File([blob], `${baseName}${ext}`, {
                type: mimeType,
                lastModified: Date.now(),
              }),
            );
          };

          recorder.onerror = () => fallback(new Error('recorder error'));
          onPhaseChange?.('compressing');
          recorder.start(200);

          const drawFrame = () => {
            if (video.ended || video.currentTime >= duration - 0.05) {
              stopRecorder();
              return;
            }

            ctx.drawImage(video, 0, 0, width, height);
            if (onProgress) {
              onProgress(Math.min(98, Math.round((video.currentTime / duration) * 100)));
            }
            animationId = requestAnimationFrame(drawFrame);
          };

          drawFrame();
        })
        .catch(error => fallback(error));
    };

    video.src = objectUrl;
  });
}
